
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  useTheme,
  TablePagination,
  Tooltip,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  RemoveRedEyeOutlined as RemoveRedEyeOutlinedIcon,
  History as HistoryIcon,
  SignalCellularAlt as SignalCellularAltIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Save as SaveIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useGlobalConfig } from 'context/GlobalContext';
import { BglPrimaryButton } from 'components/button/BglButtons';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { AddNewUserComponent } from '../workflow/AddNewUserComponent';
import { BglOverlayLoader } from '../../components/loading/BglOverlayLoader';
import { PilotApis } from 'apis/pilotAPI';
import { ClusterApis } from 'apis/clusterAPI';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId, selectPilotList } from 'store/dataReducers/pilotsSlice';
import { useToast } from '../../context/ToastContext';
import { useLocation } from 'react-router-dom';
import { CustomerDataApis } from 'apis/customerDataAPIs';
import { EmailTriggerDialog, type EmailSession, type ApplicableEmail } from './EmailTriggerDialog';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

interface CustomerData {
  id: string;
  firstName: string;
  lastName: string;
  primaryEmail: string;
  secondaryEmail: string;
  meterType: string;
  dcxClusters: string[];
  ratePlan: string;
  billingCycle: string;
  bbDuration: string;
  address: string;
  zipCode: string;
  personaName: string;
  personaDescription: string;
  sourcePilotId: string;
  sourceUUID: string;
  uuid: string;
  createdAt: string;
  ingestionStatus?: string; 
}

export const CustomerDataGeneratorPage = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const location = useLocation();
  const toast = useToast();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList);
  const [customerData, setCustomerData] = useState<CustomerData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Editing states
  const [editingField, setEditingField] = useState<{ rowId: string; field: string } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [editArray, setEditArray] = useState<string[]>([]);

  // Modal states
  const [dcxClusterModalOpen, setDcxClusterModalOpen] = useState(false);
  const [ratePlanModalOpen, setRatePlanModalOpen] = useState(false);
  const [selectedUserForModal, setSelectedUserForModal] = useState<CustomerData | null>(null);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<CustomerData | null>(null);

  // Data for modals
  const [clusters, setClusters] = useState<string[]>([]);
  const [clusterNameToId, setClusterNameToId] = useState<Record<string, string>>({});
  const [ratePlans, setRatePlans] = useState<string[]>([]);
  const [clustersLoading, setClustersLoading] = useState(false);
  const [ratePlansLoading, setRatePlansLoading] = useState(false);

  // Rate plan modal specific states
  const [selectedRatePlan, setSelectedRatePlan] = useState<string>('');
  const [ratePlanHistory, setRatePlanHistory] = useState<any[]>([]);
  const [rateHistoryLoading, setRateHistoryLoading] = useState(false);

  // Ingestion status states
  const [ingestionStatuses, setIngestionStatuses] = useState<Record<string, {
    user: string;
    raw: string;
    billing: string;
  }>>({});
  
  // Ingestion history states
  const [ingestionHistory, setIngestionHistory] = useState<Record<string, {
    user: any[];
    raw: any[];
    billing: any[];
  }>>({});
  const [userAttributes, setUserAttributes] = useState<Record<string, string[]>>({});
  const [userAttributesLoading, setUserAttributesLoading] = useState(false);
  
  // Ingestion status modal states
  const [ingestionModalOpen, setIngestionModalOpen] = useState(false);
  const [selectedUserForIngestionModal, setSelectedUserForIngestionModal] = useState<CustomerData | null>(null);
  const [selectedJobType, setSelectedJobType] = useState<'user' | 'raw' | 'billing'>('user');
  const [ingestionHistoryLoading, setIngestionHistoryLoading] = useState(false);
  const [selectedMeterFuel, setSelectedMeterFuel] = useState<string>('');
  const [consumptionData, setConsumptionData] = useState<any[]>([]);
  const [consumptionLoading, setConsumptionLoading] = useState(false);
  const [selectedJobTypeForModal, setSelectedJobTypeForModal] = useState<string>('raw_data_sync');
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);
  
  const chartScrollContainerRef = useRef<HTMLDivElement>(null);
  
  const [selectedProjectCache, setSelectedProjectCache] = useState<any>(null);
  const [copyingUserId, setCopyingUserId] = useState<string | null>(null);

  const [emailSessions, setEmailSessions] = useState<Record<string, EmailSession>>({});
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedUserForEmail, setSelectedUserForEmail] = useState<CustomerData | null>(null);
  const [applicableEmails, setApplicableEmails] = useState<ApplicableEmail[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsError, setEmailsError] = useState<string | null>(null);


  const toEmailLabel = (s: string) =>
    s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const handleOpenEmailDialog = async (user: CustomerData) => {
    setSelectedUserForEmail(user);
    setEmailDialogOpen(true);

    const existingSession = emailSessions[user.id];
    if (existingSession) {
      setApplicableEmails(
        existingSession.selectedEmailIds.map((id) => ({ id, label: toEmailLabel(id) }))
      );

      const projectId = selectedPilotId?.toString() || '';
      try {
        const statusRes = await CustomerDataApis.getEmailStatus(rc, {
          project_id: projectId,
          uuid: user.uuid
        });

        if (statusRes.success) {
          const payload = statusRes.data?.data ?? statusRes.data;
          const statusMap: Record<string, string> = payload?.status ?? {};
          const summary = payload?.summary;
          const record = payload?.record;
          const updatedAt = record?.updatedAt;

          const updatedResults: EmailSession['results'] = {};
          existingSession.selectedEmailIds.forEach((id) => {
            const rawStatus = (statusMap[id] ?? '').toUpperCase();
            if (rawStatus === 'SENT' || rawStatus === 'SUCCESS' || rawStatus === 'COMPLETED') {
              updatedResults[id] = { status: 'success', timestamp: updatedAt ?? new Date().toISOString() };
            } else if (rawStatus === 'FAILED' || rawStatus === 'ERROR') {
              updatedResults[id] = { status: 'failed', error: 'Something went wrong. Please contact Delivery Team', timestamp: updatedAt ?? new Date().toISOString() };
            } else {
              updatedResults[id] = { status: 'sending' };
            }
          });

          const allDone = summary?.isCompleted === true;

          setEmailSessions((prev) => ({
            ...prev,
            [user.id]: {
              ...existingSession,
              results: updatedResults,
              isProcessing: !allDone
            }
          }));

          if (!allDone) {
            pollEmailStatus(user.id, user.uuid, projectId, existingSession.selectedEmailIds);
          }
        }
      } catch (e) {
        console.error('Error fetching email status on dialog open:', e);
      }

      return;
    }

    fetchApplicableEmails(user.uuid);
  };

  const pollEmailStatus = useCallback(async (userId: string, uuid: string, projectId: string, emailIds: string[]) => {
    // Phase 1: every 15s for the first 60s (4 polls)
    // Phase 2: every 60s for the next 5 min (5 polls)
    const schedule = [
      ...Array(4).fill(15000),
      ...Array(5).fill(60000)
    ];

    for (let i = 0; i < schedule.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, schedule[i]));

      try {
        const statusRes = await CustomerDataApis.getEmailStatus(rc, {
          project_id: projectId,
          uuid
        });

        if (!statusRes.success) continue;

        const payload = statusRes.data?.data ?? statusRes.data;
        const statusMap: Record<string, string> = payload?.status ?? {};
        const summary = payload?.summary;
        const record = payload?.record;
        const updatedAt = record?.updatedAt;

        const updatedResults: EmailSession['results'] = {};

        emailIds.forEach((id) => {
          const rawStatus = (statusMap[id] ?? '').toUpperCase();

          if (rawStatus === 'SENT' || rawStatus === 'SUCCESS' || rawStatus === 'COMPLETED') {
            updatedResults[id] = { status: 'success', timestamp: updatedAt ?? new Date().toISOString() };
          } else if (rawStatus === 'FAILED' || rawStatus === 'ERROR') {
            updatedResults[id] = {
              status: 'failed',
              error: 'Something went wrong. Please contact Delivery Team',
              timestamp: updatedAt ?? new Date().toISOString()
            };
          } else if (rawStatus === 'PENDING' || rawStatus === 'PROCESSING' || rawStatus === 'RUNNING') {
            updatedResults[id] = { status: 'sending' };
          } else {
            updatedResults[id] = { status: 'sending' };
          }
        });

        const allDone = summary?.isCompleted === true;

        setEmailSessions((prev) => ({
          ...prev,
          [userId]: {
            selectedEmailIds: emailIds,
            results: updatedResults,
            isProcessing: !allDone,
            startTime: prev[userId]?.startTime
          }
        }));

        if (allDone) return;
      } catch {
        // continue polling on transient errors
      }
    }

    setEmailSessions((prev) => {
      const session = prev[userId];
      if (!session) return prev;
      const timedOutResults: EmailSession['results'] = {};
      emailIds.forEach((id) => {
        const existing = session.results[id];
        timedOutResults[id] = existing?.status === 'success' || existing?.status === 'failed'
          ? existing
          : { status: 'failed', error: 'Timed out waiting for status', timestamp: new Date().toISOString() };
      });
      return {
        ...prev,
        [userId]: { ...session, results: timedOutResults, isProcessing: false }
      };
    });
  }, [rc]);

  const handleStartEmailDispatch = async (emailIds: string[]) => {
    if (!selectedUserForEmail) return;
    const userId = selectedUserForEmail.id;
    const uuid = selectedUserForEmail.uuid;
    const projectId = selectedPilotId?.toString() || '';

    const initialResults: EmailSession['results'] = {};
    emailIds.forEach((id) => {
      initialResults[id] = { status: 'pending' };
    });

    setEmailSessions((prev) => ({
      ...prev,
      [userId]: {
        selectedEmailIds: emailIds,
        results: initialResults,
        isProcessing: true,
        startTime: new Date().toISOString()
      }
    }));

    try {
      const response = await CustomerDataApis.triggerEmailRuns(rc, {
        uuid,
        projectId,
        emails: emailIds,
        all_events: applicableEmails.map((e) => e.id)
      });

      if (response.success) {
        const sendingResults: EmailSession['results'] = {};
        emailIds.forEach((id) => {
          sendingResults[id] = { status: 'sending' };
        });
        setEmailSessions((prev) => ({
          ...prev,
          [userId]: { ...prev[userId], results: sendingResults }
        }));

        pollEmailStatus(userId, uuid, projectId, emailIds);
      } else {
        const now = new Date().toISOString();
        const failResults: EmailSession['results'] = {};
        emailIds.forEach((id) => {
          failResults[id] = {
            status: 'failed',
            error: response.error || 'Email trigger failed',
            timestamp: now
          };
        });
        setEmailSessions((prev) => ({
          ...prev,
          [userId]: {
            selectedEmailIds: emailIds,
            results: failResults,
            isProcessing: false,
            startTime: prev[userId]?.startTime
          }
        }));
      }
    } catch (e) {
      const now = new Date().toISOString();
      const errorResults: EmailSession['results'] = {};
      emailIds.forEach((id) => {
        errorResults[id] = {
          status: 'failed',
          error: 'Unexpected error triggering emails. Please try again.',
          timestamp: now
        };
      });
      setEmailSessions((prev) => ({
        ...prev,
        [userId]: {
          selectedEmailIds: emailIds,
          results: errorResults,
          isProcessing: false,
          startTime: prev[userId]?.startTime
        }
      }));
    }
  };

  const fetchApplicableEmails = async (uuid: string) => {
    setEmailsLoading(true);
    setEmailsError(null);
    setApplicableEmails([]);
    try {
      const projectId = selectedPilotId?.toString() || '';
      const response = await CustomerDataApis.getUserEmails(rc, {
        project_id: projectId,
        uuid
      });
      if (response.success && response.data) {
        const payload = response.data?.data ?? response.data;
        const rawEmails: any[] = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.emails)
            ? payload.emails
            : [];
        const emailList: ApplicableEmail[] = rawEmails.map((item: any) => {
          if (typeof item === 'string') {
            return { id: item, label: toEmailLabel(item) };
          }
          const id = item.id ?? item.email_id ?? item.event_name ?? '';
          const label = item.label ?? item.name ?? item.display_name ?? toEmailLabel(id);
          return { id, label };
        });
        setApplicableEmails(emailList);
      } else {
        setEmailsError(response.error || 'Failed to fetch email templates');
      }
    } catch (e) {
      console.error('Error fetching user emails:', e);
      setEmailsError('Unable to fetch email templates. Please try again.');
    } finally {
      setEmailsLoading(false);
    }
  };

  const handleAcknowledgeEmailSession = async () => {
    if (!selectedUserForEmail) return;
    const projectId = selectedPilotId?.toString() || '';

    try {
      await CustomerDataApis.resetEmailSession(rc, {
        uuid: selectedUserForEmail.uuid,
        projectId
      });
    } catch (e) {
      console.error('Error resetting email session:', e);
      toast.error('Failed to reset email session on server');
    }

    setEmailSessions((prev) => {
      const next = { ...prev };
      delete next[selectedUserForEmail.id];
      return next;
    });

    fetchApplicableEmails(selectedUserForEmail.uuid);
  };

  const getEmailTriggerDisplayName = (row: CustomerData) => {
    const name = `${row.firstName} ${row.lastName}`.trim();
    const emails = [row.primaryEmail, row.secondaryEmail].filter(Boolean).join(', ');
    if (name && emails) return `${name} (${emails})`;
    return name || row.primaryEmail || 'User';
  };

  const getSelectedProject = async (): Promise<any> => {
    if (!selectedPilotId) {
      return null;
    }

    if (selectedProjectCache && String(selectedProjectCache.projectId) === String(selectedPilotId)) {
      return selectedProjectCache;
    }

    const pilotFromList = pilotList?.find((p: any) => String(p.projectId) === String(selectedPilotId));
    if (pilotFromList && (pilotFromList as any).environment?.api_host) {
      setSelectedProjectCache(pilotFromList);
      return pilotFromList;
    }

    try {
      const projectsResponse = await rc.bffClient.getProjects();
      
      if ('error' in projectsResponse) {
        console.error('Failed to fetch projects:', projectsResponse.error);
        return null;
      }

      const projects = projectsResponse.data?.data;
      const selectedProject = projects?.find((project: any) => project.projectId === selectedPilotId);
      
      if (selectedProject) {
        setSelectedProjectCache(selectedProject);
        return selectedProject;
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    }

    return null;
  };

  const getDestinationBaseUrl = async (): Promise<string> => {
    const selectedProject = await getSelectedProject();
    return selectedProject?.environment?.api_host || '';
  };

  const handleMeterFuelChange = async (value: string, user?: CustomerData) => {
    setSelectedMeterFuel(value);
    const uuid = user?.uuid || selectedUserForIngestionModal?.uuid;
    if (!uuid) return;

    // Check if the selected meter fuel is NSM - skip API call for NSM meters
    if (value && value.startsWith('NSM')) {
      setConsumptionData([]);
      setConsumptionLoading(false);
      return;
    }

    const destinationBaseUrl = await getDestinationBaseUrl();
    try {
      setConsumptionLoading(true);
      const response = await CustomerDataApis.fetchConsumptionData(rc, {
        uuid,
        destinationBaseUrl,
        meterFuel: value,
        destinationPilotId: selectedPilotId?.toString() || ''
      });

      if (response.success && response.data?.data) {
        // Filter last 3 months and process data
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        
        const filteredData = response.data.data
          .filter((item: any) => {
            if (!item.date) return false;
            const itemDate = new Date(item.date);
            return itemDate >= threeMonthsAgo;
          })
          .map((item: any) => ({
            ...item,
            consumptionKwh: item.totalConsumption / 1000, // Convert Wh to kWh
            formattedDate: new Date(item.date).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric'
            })
          }))
          .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());

        setConsumptionData(filteredData);
      } else {
        setConsumptionData([]);
        
        const errorMessage = response.data?.message || response.error || '';
        const errorStatus = response.data?.status || '';
        
        if (errorMessage.includes('403') || errorStatus === '403') {
          toast.error('Forbidden access. Please check the Access with the Product team');
        } else if (errorMessage || errorStatus === 'error' || !response.success) {
          toast.error('Unable to fetch the consumption data, Please reach out to product team for assistance');
        }
      }
    } catch (e) {
      console.error('Error fetching consumption data:', e);
      setConsumptionData([]);
      
      const errorMessage = e instanceof Error ? e.message : String(e);
      if (errorMessage.includes('403')) {
        toast.error('Forbidden access. Please check the Access with the Product team');
      } else {
        toast.error('Unable to fetch the consumption data, Please reach out to product team for assistance');
      }
    } finally {
      setConsumptionLoading(false);
    }
  };

  useEffect(() => {
    if (consumptionData.length > 0 && chartScrollContainerRef.current) {
      requestAnimationFrame(() => {
        const container = chartScrollContainerRef.current;
        if (container) {
          container.scrollLeft = container.scrollWidth;
        }
      });
    }
  }, [consumptionData]);

  // Email validation regex
  const emailRegex = /^[a-zA-Z][a-zA-Z0-9._%+-]{1,}@[a-zA-Z0-9]+\.[a-zA-Z]{2,}$/;

  // Email validation function
  const isValidEmail = (email: string): boolean => {
    if (!email || email.trim() === '') return true; 
    return emailRegex.test(email.trim());
  };

  // Name validation regex - letters (including accented), spaces, hyphens, and apostrophes allowed
  const nameRegex = /^[\p{L}\s'-]+$/u;

  // Name validation function
  const isValidName = (name: string): boolean => {
    if (!name || name.trim() === '') return true; 
    return nameRegex.test(name.trim());
  };

  // Helper function to get latest status from filtered records
  const getLatestStatus = (records: any[]): string => {
    if (!records || records.length === 0) {
      return 'not_started';
    }
    
    const validRecords = records.filter((record: any) => record && record.response_time);
    if (validRecords.length === 0) {
      return 'not_started';
    }
    
    // Sort by response_time descending to get the latest
    const sortedRecords = validRecords.sort((a: any, b: any) => 
      new Date(b.response_time).getTime() - new Date(a.response_time).getTime()
    );
    
    const latestRecord = sortedRecords[0];
    const status = latestRecord.response_payload?.status;
    
    // Return 'not_started' if status is not found or empty
    if (!status || status.trim() === '') {
      return 'not_started';
    }
    
    return status;
  };

  // Fetch ingestion status for a single user on demand
  const fetchIngestionStatusForUser = useCallback(async (user: CustomerData) => {
    if (!selectedPilotId || !user?.uuid) return;
    try {
      setIngestionHistoryLoading(true);
      // Fetch all jobs for this user and split locally by job_name
      const allRes = await CustomerDataApis.fetchUserDataSchedulerHistory(rc, {
        destination_pilot_id: String(selectedPilotId),
        uuid: user.uuid
      });
      const allRecords: any[] = (allRes.success && Array.isArray(allRes.data?.data)) ? allRes.data.data : [];
      const history = { user: [] as any[], raw: [] as any[], billing: [] as any[] };
      allRecords.forEach(rec => {
        switch (rec?.job_name) {
          case 'user_details_sync': history.user.push(rec); break;
          case 'raw_data_sync': history.raw.push(rec); break;
          case 'billing_data_sync': history.billing.push(rec); break;
        }
      });

      setIngestionHistory(prev => ({ ...prev, [user.id]: history }));
      // Do NOT update the chip status from scheduler history; chips should reflect metadata-only latest status
    } catch (e) {
      console.error('Error fetching ingestion status for user:', e);
    } finally {
      setIngestionHistoryLoading(false);
    }
  }, [selectedPilotId, rc]);

  // Handle ingestion status chip click
  const handleIngestionStatusClick = (user: CustomerData, jobType: 'user' | 'raw' | 'billing') => {
    // Reset per-user consumption state
    setSelectedMeterFuel('');
    setConsumptionData([]);
    setConsumptionLoading(false);
    setSelectedUserForIngestionModal(user);
    setSelectedJobType(jobType);
    setSelectedJobTypeForModal('raw_data_sync');
    setIngestionModalOpen(true);
    fetchIngestionStatusForUser(user);
    const meterFuelTypes = user.meterType ? user.meterType.split(', ').filter(Boolean) : [];
    if (meterFuelTypes.length > 0) {
      handleMeterFuelChange(meterFuelTypes[0], user);
    }
  };

  // Handle ingestion modal close
  const handleIngestionModalClose = () => {
    setIngestionModalOpen(false);
    setSelectedUserForIngestionModal(null);
    setSelectedJobType('user');
  };


  // Get status color for chip
  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status?.toLowerCase()) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'not_started': return 'default';
      default: return 'default';
    }
  };

  const getHoverStylesByStatus = (status: string) => {
    return {
      backgroundColor: theme.palette.action.hover,
      opacity: 0.8,
      transform: 'scale(1.05)'
    };
  };

  // Get status label for chip
  const getStatusLabel = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'success': return 'Complete';
      case 'error': return 'Incomplete';
      case 'not_started': return 'Not Available';
      default: return 'Not Available';
    }
  };

  // Fetch user metadata from API
  const fetchUserMetadata = async () => {
    try {
      setLoading(true);
      setInitialLoading(true);
      setError(null);
      setInfo(null); 
      
      if (!selectedPilotId) {
        setError('No pilot selected. Please select a pilot from the main navigation.');
        setInitialLoading(false);
        return;
      }
      
      const response = await PilotApis.getAllUserMetadata(rc, selectedPilotId.toString(), 'Success');
      
      if (response.success && response.data) {
        let userDataArray = response.data;
        const emailStatusFromApi: Record<string, any> = response.data?.email_status ?? {};
        
        if (response.data.data && Array.isArray(response.data.data)) {
          userDataArray = response.data.data;
        }
        else if (!Array.isArray(response.data)) {
          console.error('Response data is not an array:', response.data);
          setError('Invalid data format received from API');
          setInitialLoading(false);
          return;
        }
        
        if (userDataArray.length === 0) {
          setCustomerData([]);
          setInfo(`No user metadata found for pilot ID: ${selectedPilotId}. Please ensure users have been added to this pilot.`);
          setInitialLoading(false);
          return;
        }
        
        // Transform API data to match table structure using exact field mappings
        const transformedData: CustomerData[] = userDataArray.map((item: any, index: number) => {
          const meterTypeValue = item.meter_type || '';
          
          return {
            id: item.id?.toString() || '',
            firstName: item.first_name || '',                    
            lastName: item.last_name || '',                     
            primaryEmail: item.primary_email || '',            
            secondaryEmail: item.secondary_email || '',          
            meterType: meterTypeValue,                   
            dcxClusters: Array.isArray(item.dcx_clusters) ? item.dcx_clusters : [],
            ratePlan: item.rate_plan || '',                     
            billingCycle: item.billing_cycle || '',             
            bbDuration: (() => {
              const period = item.bb_trueup_period;
              if (typeof period === 'number') {
                return `${period} Month`;
              }
              const duration = item.bb_duration || item.bbDuration || '0';
              if (duration && !String(duration).toLowerCase().includes('month')) {
                return `${duration} Month`;
              }
              return duration || '0 Month';
            })(),
            address: `${item.address || ''}${item.city ? `, ${item.city}` : ''}${item.state ? `, ${item.state}` : ''}`.trim() || '',
            zipCode: item.zipcode || '',                        
            personaName: item.persona_name || '',               
            personaDescription: item.persona_description || '', 
            sourcePilotId: item.source_pilot_id || '',        
            sourceUUID: item.source_uuid || '',                 
            uuid: item.uuid || item.user_uuid || item.userUUID || item.user_id || item.userId || '',                          
            createdAt: item.created_at || item.createdAt || '',
            ingestionStatus: typeof item.ingestion_status === 'string' ? item.ingestion_status : '' 
          };
        });
        
        setCustomerData(transformedData);

        if (Object.keys(emailStatusFromApi).length > 0) {
          const uuidToId: Record<string, string> = {};
          transformedData.forEach((user) => {
            if (user.uuid) uuidToId[user.uuid] = user.id;
          });

          const restoredSessions: Record<string, EmailSession> = {};

          Object.entries(emailStatusFromApi).forEach(([uuid, record]: [string, any]) => {
            const userId = uuidToId[uuid];
            if (!userId || !record?.status) return;

            const statusMap: Record<string, string> = record.status;
            const emailIds = Object.keys(statusMap);
            if (emailIds.length === 0) return;

            const results: EmailSession['results'] = {};
            let hasActiveEmails = false;

            emailIds.forEach((emailId) => {
              const rawStatus = (statusMap[emailId] ?? '').toUpperCase();
              if (rawStatus === 'SENT' || rawStatus === 'SUCCESS' || rawStatus === 'COMPLETED') {
                results[emailId] = { status: 'success', timestamp: record.updatedAt ?? '' };
              } else if (rawStatus === 'FAILED' || rawStatus === 'ERROR') {
                results[emailId] = { status: 'failed', error: 'Something went wrong. Please contact Delivery Team', timestamp: record.updatedAt ?? '' };
              } else {
                results[emailId] = { status: 'sending' };
                hasActiveEmails = true;
              }
            });

            restoredSessions[userId] = {
              selectedEmailIds: emailIds,
              results,
              isProcessing: hasActiveEmails,
              startTime: record.createdAt
            };

            if (hasActiveEmails) {
              const projectId = selectedPilotId?.toString() || '';
              pollEmailStatus(userId, uuid, projectId, emailIds);
            }
          });

          if (Object.keys(restoredSessions).length > 0) {
            setEmailSessions((prev) => ({ ...prev, ...restoredSessions }));
          }
        }

        setInitialLoading(false);      
        if (transformedData.length > 0) {
          try {
            setUserAttributesLoading(true);
            const response = await CustomerDataApis.fetchUserAttribute(rc, {
              destination_pilot_id: String(selectedPilotId)
            });

            if (response.success && response.data) {
              const results = response.data?.results || [];
              
              const attributesMap: Record<string, string[]> = {};
              
              results.forEach((item: any) => {
                const uuid = item?.uuid;
                if (uuid) {
                  const attrs = Array.isArray(item?.attributes) ? item.attributes : [];
                  attributesMap[uuid] = attrs;
                }
              });

              const userAttributesMap: Record<string, string[]> = {};
              transformedData.forEach(user => {
                const attrs = attributesMap[user.uuid] || [];
                userAttributesMap[user.id] = attrs;
              });

              setUserAttributes(userAttributesMap);
            }
          } catch (error) {
            console.error('Error fetching user attributes in batch:', error);
          } finally {
            setUserAttributesLoading(false);
          }
        }
      } else {
        console.error('Response not successful or no data:', response);
        setInitialLoading(false);
        
        // Handle "No user metadata found" case from response payload
        if (response.data?.message?.includes('No user metadata found for pilot ID')) {
          setInfo(response.data.message);
        } else {
          setError('Failed to fetch user metadata');
        }
      }
    } catch (error: any) {
      console.error('Error fetching user metadata:', error);
      setError('Failed to fetch user metadata');
      setInitialLoading(false);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    setSelectedProjectCache(null);
    
    if (selectedPilotId) {
      fetchUserMetadata();
    } else {
      setCustomerData([]);
      setError('No pilot selected. Please select a pilot from the main navigation.');
      setInitialLoading(false);
    }
  }, [selectedPilotId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const addUser = searchParams.get('addUser');
    if (addUser === 'true') {
      setShowAddUserForm(true);
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('addUser');
      const newSearch = newSearchParams.toString();
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search, location.pathname]);

  // Removed useEffect for fetchAllUserAttributes - now called directly in fetchUserMetadata

  // Removed bulk ingestion history fetch; fetch on demand per user when chip clicked

  const handleDelete = async (id: string) => {
    const user = customerData.find(item => item.id === id);
    if (user) {
      setUserToDelete(user);
      setDeleteConfirmModalOpen(true);
    }
  };

  const handleAddNewUser = () => {
    setShowAddUserForm(true);
  };

  const handleBackToTable = () => {
    setShowAddUserForm(false);
  };

  const handleRefresh = async () => {
    setError(null);
    setInfo(null);
    try {
      setLoading(true);
      setError(null);
      setInfo(null); 
      
      if (!selectedPilotId) {
        setError('No pilot selected. Please select a pilot from the main navigation.');
        return;
      }
      
      const response = await PilotApis.getAllUserMetadata(rc, selectedPilotId.toString(), 'Success');
      
      if (response.success && response.data) {
        // Check if response.data is an array or has a data property
        let userDataArray = response.data;
        
        // If response.data.data exists, use that (nested structure)
        if (response.data.data && Array.isArray(response.data.data)) {
          userDataArray = response.data.data;
        }
        // If response.data is not an array, check if it's wrapped
        else if (!Array.isArray(response.data)) {
          console.error('Response data is not an array:', response.data);
          setError('Invalid data format received from API');
          return;
        }
        
        if (userDataArray.length === 0) {
          setCustomerData([]);
          setInfo(`No user metadata found for pilot ID: ${selectedPilotId}. Please ensure users have been added to this pilot.`);
          return;
        }
        
        // Transform API data to match table structure using exact field mappings
        const transformedData: CustomerData[] = userDataArray.map((item: any, index: number) => {
          const meterTypeValue = item.meter_type || '';
          
          return {
            id: item.id?.toString() || '',
            firstName: item.first_name || '',                    
            lastName: item.last_name || '',                     
            primaryEmail: item.primary_email || '',            
            secondaryEmail: item.secondary_email || '',          
            meterType: meterTypeValue,                   
            dcxClusters: Array.isArray(item.dcx_clusters) ? item.dcx_clusters : [],
            ratePlan: item.rate_plan || '',                     
            billingCycle: item.billing_cycle || '',             
            bbDuration: (() => {
              const period = item.bb_trueup_period;
              if (typeof period === 'number') {
                return `${period} Month`;
              }
              const duration = item.bb_duration || item.bbDuration || '0';
              if (duration && !String(duration).toLowerCase().includes('month')) {
                return `${duration} Month`;
              }
              return duration || '0 Month';
            })(),
            address: `${item.address || ''}${item.city ? `, ${item.city}` : ''}${item.state ? `, ${item.state}` : ''}`.trim() || '',
            zipCode: item.zipcode || '',                        
            personaName: item.persona_name || '',               
            personaDescription: item.persona_description || '', 
            sourcePilotId: item.source_pilot_id || '',        
            sourceUUID: item.source_uuid || '',                 
            uuid: item.uuid || item.user_uuid || item.userUUID || item.user_id || item.userId || '',                          
            createdAt: item.created_at || item.createdAt || '',
            ingestionStatus: typeof item.ingestion_status === 'string' ? item.ingestion_status : '' 
          };
        });
        
        setCustomerData(transformedData);
        

        window.location.reload();
      } else {
        console.error('Response not successful or no data:', response);
        
        // Handle "No user metadata found" case from response payload
        if (response.data?.message?.includes('No user metadata found for pilot ID')) {
          setInfo(response.data.message);
        } else {
          setError('Failed to fetch user metadata');
        }
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Error fetching user metadata:', error);
      setError('Failed to fetch user metadata');
      setLoading(false);
    }
  };

  // Handle preview dashboard
  const handlePreviewDashboard = async (user: CustomerData) => {
    try {

      console.log('Using UUID for preview:', user.uuid);

      const response = await PilotApis.generateUserWebDashboard(
        rc, 
        user.uuid, 
        selectedPilotId?.toString()
      );

      if (response.success && response.data?.dashboard_url) {
        // Open the dashboard URL in a new tab
        window.open(response.data.dashboard_url, '_blank');
      } else {
        toast.error(`Failed to generate preview: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    }
  };

  // Handle copy dashboard URL
  const handleCopyDashboardUrl = async (user: CustomerData) => {
    try {
      if (!selectedPilotId || !user.uuid) {
        toast.error('Missing required information to generate dashboard URL');
        return;
      }

      setCopyingUserId(user.id);

      const response = await PilotApis.generateUserWebDashboard(
        rc, 
        user.uuid, 
        selectedPilotId.toString()
      );

      if (response.success && response.data?.dashboard_url) {
        await navigator.clipboard.writeText(response.data.dashboard_url);
        toast.success('Dashboard URL copied to clipboard');
      } else {
        toast.error(`Failed to generate dashboard URL: ${response.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error copying dashboard URL:', error);
      toast.error('Failed to copy dashboard URL');
    } finally {
      setCopyingUserId(null);
    }
  };

  // Fetch clusters for DCX cluster modal
  const fetchClusters = async () => {
    try {
      setClustersLoading(true);
      
      const selectedPilot = pilotList.find(pilot => String(pilot.projectId) === String(selectedPilotId));
      
      if (!selectedPilot || !selectedPilot.projectId) {
        console.warn('Selected pilot not found or missing projectId');
        return;
      }
      
      const response = await ClusterApis.getClusters(rc, selectedPilot.projectId);
      
      if (response.success && response.data) {
        const clusterData = response.data;
        let physicalClusterNames: string[] = [];
        const nameToId: Record<string, string> = {};

        // Prefer original_data payload with categories
        const payload = clusterData?.original_data?.payload;
        if (Array.isArray(payload)) {
          payload.forEach((item: any) => {
            const itemClusters = item?.clusters;
            if (Array.isArray(itemClusters)) {
              itemClusters.forEach((c: any) => {
                const category = String(c?.clusterCategory || '').toLowerCase();
                const clusterReference = c?.clusterReference || '';
                if (category === 'physical' && clusterReference !== 'DEFAULT') {
                  const name = c?.clusterName || c?.name || c?.cluster_name || 'Unknown Cluster';
                  const id = c?.clusterId || c?.id || c?.cluster_id;
                  if (name && id && !nameToId[name]) {
                    nameToId[name] = String(id);
                    physicalClusterNames.push(name);
                  }
                }
              });
            }
          });
        }
        
        if (physicalClusterNames.length === 0 && clusterData.clusters && Array.isArray(clusterData.clusters)) {
          clusterData.clusters.forEach((c: any) => {
            const category = String(c?.clusterCategory || '').toLowerCase();
            const clusterReference = c?.clusterReference || '';
            if (category === 'physical' && clusterReference !== 'DEFAULT') {
              const name = c?.clusterName || c?.name || c?.cluster_name || 'Unknown Cluster';
              const id = c?.clusterId || c?.id || c?.cluster_id;
              if (name && id && !nameToId[name]) {
                nameToId[name] = String(id);
                physicalClusterNames.push(name);
              }
            }
          });
        }

        setClusters(physicalClusterNames);
        setClusterNameToId(nameToId);
      } else {
        console.warn('Failed to fetch clusters:', response.error);
      }
    } catch (error) {
      console.error('Error fetching clusters:', error);
    } finally {
      setClustersLoading(false);
    }
  };

  // Fetch rate plans for rate plan modal
  const fetchRatePlans = async () => {
    try {
      setRatePlansLoading(true);
      
      const selectedProject = await getSelectedProject();
      
      if (!selectedProject) {
        console.warn('Selected project not found');
        return;
      }

      const apiHost = selectedProject.environment?.api_host;
      const utilityId = selectedProject.projectId;
      
      if (!apiHost || !utilityId) {
        console.warn('Missing api_host or projectId for rate plans');
        return;
      }

      const response = await PilotApis.fetchRatePlans(rc, apiHost, utilityId);
      
      if (response.success && response.data) {
        const ratePlanData = response.data;
        let ratePlanNames: string[] = [];
        
        if(ratePlanData.data && ratePlanData.data.payload && Array.isArray(ratePlanData.data.payload)) {
          ratePlanNames = ratePlanData.data.payload.map((plan: any) => {
            const planName = plan.planName || 'Unknown Rate Plan';
            const planDescription = plan.planDescription || plan.description || '';
            return planDescription ? `${planName}-${planDescription}` : planName;
          });
        } else if(ratePlanData.payload && Array.isArray(ratePlanData.payload)) {
          ratePlanNames = ratePlanData.payload.map((plan: any) => {
            const planName = plan.planName || 'Unknown Rate Plan';
            const planDescription = plan.planDescription || plan.description || '';
            return planDescription ? `${planName}-${planDescription}` : planName;
          });
        } else if(ratePlanData.data && Array.isArray(ratePlanData.data)) {
          ratePlanNames = ratePlanData.data.map((rate: any) => {
            const planName = rate.ratePlanName || rate.name || rate.id || 'Unknown Rate Plan';
            const planDescription = rate.ratePlanDescription || rate.description || '';
            return planDescription ? `${planName}-${planDescription}` : planName;
          });
        }
        
        setRatePlans(ratePlanNames);
      } else {
        console.warn('Failed to fetch rate plans:', response.error);
      }
    } catch (error) {
      console.error('Error fetching rate plans:', error);
    } finally {
      setRatePlansLoading(false);
    }
  };

  const fetchRateHistory = async (user: CustomerData) => {
    try {
      setRateHistoryLoading(true);
      
      if (!selectedPilotId || !user.uuid) {
        console.warn('Missing pilot ID or user UUID for rate history fetch');
        return;
      }

      const response = await CustomerDataApis.fetchRateHistory(rc, {
        destination_pilot_id: selectedPilotId.toString(),
        uuid: user.uuid
      });

      if (response.success && response.data) {
        const historyData = response.data;
        
        // Transform the API response to match the expected format
        if (historyData.transition_history && Array.isArray(historyData.transition_history)) {
          const transformedHistory = historyData.transition_history.map((transition: any) => ({
            planName: transition.rate_plan,
            assignedDate: transition.start_date ? new Date(transition.start_date).toLocaleDateString() : 'Unknown Date',
            endDate: transition.end_date ? new Date(transition.end_date).toLocaleDateString() : null,
            description: transition.end_date ? 
              `Rate plan effective from ${new Date(transition.start_date).toLocaleDateString()} to ${new Date(transition.end_date).toLocaleDateString()}` :
              `Previous rate plan since ${new Date(transition.start_date).toLocaleDateString()}`,
            isCurrent: !transition.end_date
          }));
          
          // Filter out current rate plan (end_date is null), only keep expired rates for history
          const expiredRates = transformedHistory.filter((history: any) => history.endDate !== null);
          setRatePlanHistory(expiredRates);
        } else {
          // If no history found, set empty array since we only show expired rates
          setRatePlanHistory([]);
        }
      } else {
        console.warn('Failed to fetch rate history:', response.error);
        // Set empty array on error since we only show expired rates
        setRatePlanHistory([]);
      }
    } catch (error) {
      console.error('Error fetching rate history:', error);
      // Set empty array on error since we only show expired rates
      setRatePlanHistory([]);
    } finally {
      setRateHistoryLoading(false);
    }
  };

  // Handle edit start
  const handleEditStart = (rowId: string, field: string, currentValue: string | string[]) => {
    setEditingField({ rowId, field });
    if (Array.isArray(currentValue)) {
      setEditArray([...currentValue]);
      setEditValue('');
    } else {
      setEditValue(currentValue);
      setEditArray([]);
    }
  };

  // Handle edit save
  const handleEditSave = async (rowId: string, field: string, value?: string) => {
    try {
      // For bbDuration, use the provided value directly
      const fieldValue = field === 'bbDuration' && value ? value : (Array.isArray(editArray) && editArray.length > 0 ? editArray : editValue);
      
      // Email validation for email fields
      if (field === 'primaryEmail' && editValue && !isValidEmail(editValue)) {
        toast.error('Invalid primary email format');
        return;
      }
      
      if (field === 'secondaryEmail' && editValue && !isValidEmail(editValue)) {
        toast.error('Invalid secondary email format');
        return;
      }
      
      const userToUpdate = customerData.find(row => row.id === rowId);
      if (!userToUpdate) {
        toast.error('User not found');
        return;
      }

      if (!selectedPilotId || !userToUpdate.uuid) {
        toast.error('Missing required information to update user');
        return;
      }

      if (field === 'secondaryEmail') {
        const updateSecondaryEmailResponse = await CustomerDataApis.updateSecondaryEmail(rc, {
          uuid: userToUpdate.uuid,
          destination_pilot_id: String(selectedPilotId),
          secondary_email: editValue || ''
        });

        if (updateSecondaryEmailResponse.success) {
          const responseMessage = updateSecondaryEmailResponse.data?.message || 'Secondary email updated successfully';
          toast.success(responseMessage);
          const updatedSecondaryEmail = updateSecondaryEmailResponse.data?.secondary_email ?? editValue;
          const finalUpdatedData = customerData.map(row => {
            if (row.id === rowId) {
              return { ...row, secondaryEmail: updatedSecondaryEmail };
            }
            return row;
          });
          setCustomerData(finalUpdatedData);
        } else {
          const errorMessage = updateSecondaryEmailResponse.error || 'Failed to update secondary email';
          toast.error(errorMessage);
        }
        
        setEditingField(null);
        setEditValue('');
        setEditArray([]);
        return;
      }

      // CRITICAL: firstName, lastName, primaryEmail MUST use updateNameEmail API, NEVER saveUserMetadata
      if (field === 'firstName' || field === 'lastName' || field === 'primaryEmail') {
        const firstName = field === 'firstName' ? editValue : userToUpdate.firstName || '';
        const lastName = field === 'lastName' ? editValue : userToUpdate.lastName || '';
        const primaryEmail = field === 'primaryEmail' ? editValue : userToUpdate.primaryEmail || '';

        if (!firstName || firstName.trim() === '') {
          toast.error('First name is required and cannot be empty');
          setEditingField(null);
          setEditValue('');
          setEditArray([]);
          return; // Early return - prevents any further execution
        }

        if (!isValidName(firstName)) {
          toast.error('First name can only contain letters, spaces, hyphens, and apostrophes');
          setEditingField(null);
          setEditValue('');
          setEditArray([]);
          return; 
        }

        if (lastName && !isValidName(lastName)) {
          toast.error('Last name can only contain letters, spaces, hyphens, and apostrophes');
          setEditingField(null);
          setEditValue('');
          setEditArray([]);
          return;
        }

        if (!primaryEmail || primaryEmail.trim() === '') {
          toast.error('Primary email is required and cannot be empty');
          setEditingField(null);
          setEditValue('');
          setEditArray([]);
          return; // Early return - prevents any further execution
        }

        if (!isValidEmail(primaryEmail)) {
          toast.error('Primary email format is invalid');
          setEditingField(null);
          setEditValue('');
          setEditArray([]);
          return; // Early return - prevents any further execution
        }

        // CRITICAL: Using updateNameEmail API - this is the ONLY API call for these fields
        const updateNameEmailResponse = await CustomerDataApis.updateNameEmail(rc, {
          uuid: userToUpdate.uuid,
          destination_pilot_id: String(selectedPilotId),
          first_name: firstName,
          last_name: lastName,
          primary_email: primaryEmail
        });

        if (updateNameEmailResponse.success) {
          const updatedData = customerData.map(row => {
            if (row.id === rowId) {
              return {
                ...row,
                firstName: firstName,
                lastName: lastName,
                primaryEmail: primaryEmail
              };
            }
            return row;
          });
          setCustomerData(updatedData);
          toast.success(`${field} updated successfully`);
        } else {
          let errorMessage = 'Failed to update metadata';
          
          if (updateNameEmailResponse.data?.message) {
            const msg = updateNameEmailResponse.data.message;
            if (typeof msg === 'string') {
              errorMessage = msg;
            } else if (msg && typeof msg === 'object' && 'message' in msg) {
              errorMessage = (msg as any).message;
            }
          } 
          else if (updateNameEmailResponse.error) {
            const err = updateNameEmailResponse.error;
            if (typeof err === 'string') {
              errorMessage = err;
            } else if (err && typeof err === 'object' && 'message' in err) {
              errorMessage = (err as any).message;
            }
          }
          
          toast.error(errorMessage);
        }

        setEditingField(null);
        setEditValue('');
        setEditArray([]);
        return; // CRITICAL: Early return - ensures we NEVER reach saveUserMetadata block below
      }

      // CRITICAL: address, zipCode MUST use updateAddressZipcode API, NEVER saveUserMetadata
      if (field === 'address' || field === 'zipCode') {
        const address = field === 'address' ? editValue : userToUpdate.address || '';
        const zipcode = field === 'zipCode' ? editValue : userToUpdate.zipCode || '';

        // CRITICAL: Using updateAddressZipcode API - this is the ONLY API call for these fields
        const updateAddressZipcodeResponse = await CustomerDataApis.updateAddressZipcode(rc, {
          uuid: userToUpdate.uuid,
          destination_pilot_id: String(selectedPilotId),
          address: address,
          zipcode: zipcode
        });

        if (updateAddressZipcodeResponse.success) {
          const updatedData = customerData.map(row => {
            if (row.id === rowId) {
              return {
                ...row,
                address: address,
                zipCode: zipcode
              };
            }
            return row;
          });
          setCustomerData(updatedData);
          toast.success(`${field} updated successfully`);
        } else {
          let errorMessage = 'Failed to update metadata';
          
          if (updateAddressZipcodeResponse.data?.message) {
            const msg = updateAddressZipcodeResponse.data.message;
            if (typeof msg === 'string') {
              errorMessage = msg;
            } else if (msg && typeof msg === 'object' && 'message' in msg) {
              errorMessage = (msg as any).message;
            }
          } 
          else if (updateAddressZipcodeResponse.error) {
            const err = updateAddressZipcodeResponse.error;
            if (typeof err === 'string') {
              errorMessage = err;
            } else if (err && typeof err === 'object' && 'message' in err) {
              errorMessage = (err as any).message;
            }
          }
          
          toast.error(errorMessage);
        }

        setEditingField(null);
        setEditValue('');
        setEditArray([]);
        return; // CRITICAL: Early return - ensures we NEVER reach saveUserMetadata block below
      }

      // ONLY personaName, personaDescription, dcxClusters should use saveUserMetadata API
      if (field === 'personaName' || field === 'personaDescription' || field === 'dcxClusters') {
        const searchParams = new URLSearchParams(location.search);
        const selectedEnv = searchParams.get('env') || 'NA';
        
        const dcxClustersForSave = (field === 'dcxClusters' ? editArray : userToUpdate.dcxClusters)
          ?.map((name: string) => {
            const id = clusterNameToId[name];
            return id ? `${name} - ${id}` : name;
          })
          .join(',') || '';

        const requestData = {
          first_name: userToUpdate.firstName || '',
          last_name: userToUpdate.lastName || '',
          rate_plan: userToUpdate.ratePlan || '',
          billing_cycle: userToUpdate.billingCycle || '',
          primary_email: userToUpdate.primaryEmail || '',
          secondary_email: userToUpdate.secondaryEmail || '',
          address: userToUpdate.address || '',
          meter_type: userToUpdate.meterType || '',
          dcx_clusters: dcxClustersForSave,
          zipcode: userToUpdate.zipCode || '',
          persona_name: field === 'personaName' ? editValue : userToUpdate.personaName || '',
          persona_description: field === 'personaDescription' ? editValue : userToUpdate.personaDescription || '',
          source_uuid: userToUpdate.sourceUUID || userToUpdate.uuid,
          source_pilot_id: userToUpdate.sourcePilotId || selectedPilotId,
          destination_pilot_id: selectedPilotId,
          source_env: selectedEnv
        };
        
        // Validate required fields before saving
        if (!requestData.rate_plan || requestData.rate_plan.trim() === '') {
          toast.error('Rate plan is required and cannot be empty');
          return;
        }
        
        if (!requestData.first_name || requestData.first_name.trim() === '') {
          toast.error('First name is required and cannot be empty');
          return;
        }
        
        if (!requestData.primary_email || requestData.primary_email.trim() === '') {
          toast.error('Primary email is required and cannot be empty');
          return;
        }
        
        if (requestData.primary_email && !isValidEmail(requestData.primary_email)) {
          toast.error('Primary email format is invalid');
          return;
        }
                
        const saveMetadataResponse = await PilotApis.saveUserMetadata(rc, requestData);

        if (saveMetadataResponse.success) {
          const updatedData = customerData.map(row => {
            if (row.id === rowId) {
              const newValue = field === 'dcxClusters' ? editArray : editValue;
              return { ...row, [field]: newValue };
            }
            return row;
          });
          setCustomerData(updatedData);
          toast.success(`${field} updated successfully`);
        } else {
          let errorMessage = 'Failed to save metadata';
          
          if (saveMetadataResponse.data?.message) {
            const msg = saveMetadataResponse.data.message;
            if (typeof msg === 'string') {
              errorMessage = msg;
            } else if (msg && typeof msg === 'object' && 'message' in msg) {
              errorMessage = (msg as any).message;
            }
          } 
          else if (saveMetadataResponse.error) {
            const err = saveMetadataResponse.error;
            if (typeof err === 'string') {
              errorMessage = err;
            } else if (err && typeof err === 'object' && 'message' in err) {
              errorMessage = (err as any).message;
            }
          }
          
          toast.error(errorMessage);
        }

        setEditingField(null);
        setEditValue('');
        setEditArray([]);
        return;
      }

      // If field is not handled above, show error
      toast.error(`Field ${field} is not supported for editing`);
      
      setEditingField(null);
      setEditValue('');
      setEditArray([]);
    } catch (error: any) {
      console.error('Error saving edit:', error);
      
      // Handle API error responses
      let errorMessage = 'Failed to save changes';
      
      if (error.response?.data?.message) {
        const msg = error.response.data.message;
        if (typeof msg === 'string') {
          errorMessage = msg;
        } else if (msg && typeof msg === 'object' && 'message' in msg) {
          errorMessage = (msg as any).message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      
      setEditingField(null);
      setEditValue('');
      setEditArray([]);
    }
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingField(null);
    setEditValue('');
    setEditArray([]);
  };

  // Handle DCX cluster modal
  const handleDcxClusterModalOpen = (user: CustomerData) => {
    setSelectedUserForModal(user);
    const normalized = Array.isArray(user.dcxClusters)
      ? user.dcxClusters.map((c: string) => c && c.includes(' - ') ? c.split(' - ')[0] : c)
      : (user.dcxClusters ? String(user.dcxClusters).split(',').map(s => s.trim()).map((c: string) => c && c.includes(' - ') ? c.split(' - ')[0] : c) : []);
    setEditArray(normalized);
    setDcxClusterModalOpen(true);
    fetchClusters();
  };

  const handleDcxClusterModalClose = () => {
    setDcxClusterModalOpen(false);
    setSelectedUserForModal(null);
    setEditArray([]);
  };

  const handleDcxClusterToggle = (cluster: string) => {
    setEditArray(prev => {
      if (prev.includes(cluster)) {
        return prev.filter(c => c !== cluster);
      } else {
        return [...prev, cluster];
      }
    });
  };

  const handleDcxClusterSave = async () => {
    if (selectedUserForModal) {
      await handleEditSave(selectedUserForModal.id, 'dcxClusters');
      handleDcxClusterModalClose();
    }
  };

  // Handle rate plan modal
  const handleRatePlanModalOpen = (user: CustomerData) => {
    
    setSelectedUserForModal(user);
    setSelectedRatePlan(''); 
      
    setRatePlanModalOpen(true);
    fetchRatePlans();
    fetchRateHistory(user); 
  };

  const handleRatePlanModalClose = () => {
    setRatePlanModalOpen(false);
    setSelectedUserForModal(null);
    setSelectedRatePlan('');
  };

  const handleRatePlanSave = async () => {
    console.log('handleRatePlanSave called with:', { selectedUserForModal, selectedRatePlan });
    
    if (selectedUserForModal && selectedRatePlan) {
      if (!selectedUserForModal.uuid) {
        toast.error('User UUID is required to update rate plan');
        return;
      }

      if (!selectedPilotId) {
        toast.error('Pilot ID is required to update rate plan');
        return;
      }
            
      // Set loading state
      setLoading(true);
      
      try {
        const rateTransitionResponse = await CustomerDataApis.rateTransition(rc, {
          uuid: selectedUserForModal.uuid,
          destination_pilot_id: String(selectedPilotId),
          rate_plan: selectedRatePlan
        });

        console.debug('Rate transition request data:', {
          uuid: selectedUserForModal.uuid,
          destination_pilot_id: String(selectedPilotId),
          rate_plan: selectedRatePlan
        });

        if (rateTransitionResponse.success) {
          const updatedData = customerData.map(row => {
            if (row.id === selectedUserForModal.id) {
              return { ...row, ratePlan: selectedRatePlan };
            }
            return row;
          });
          
          setCustomerData(updatedData);
          
          await fetchRateHistory(selectedUserForModal);
          
          toast.success(`Rate plan successfully updated to ${selectedRatePlan}`);
          handleRatePlanModalClose();
        } else {
          toast.error(`Failed to update rate plan: ${rateTransitionResponse.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error saving rate plan:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        toast.error(`Failed to save rate plan changes: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const filteredData = customerData?.filter(item =>
    Object.values(item).some(value =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  ) || [];

  const sortedData = filteredData?.sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  }) || [];

  const paginatedData = sortedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  ) || [];

  // Conditional rendering based on showAddUserForm state
  if (showAddUserForm) {
    return <AddNewUserComponent onBack={handleBackToTable} />;
  }

  // Render Main Table View
  return (
    <Box sx={{ 
      bgcolor: theme.palette.background.default, 
      minHeight: '100vh', 
      py: 4,
      position: 'relative'
    }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Manage Users Section */}
        <Card sx={{ 
          p: 0, 
          borderRadius: 3, 
          boxShadow: 3,
          bgcolor: theme.palette.background.paper
        }}>
        {/* Manage Users Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          px: 4, 
          pt: 4 
        }}>
          <Box sx={{ display: 'flex', flexDirection: "column" }}>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <PeopleAltOutlinedIcon sx={{ fontSize: 38, marginRight: 1, color: theme.palette.primary.main }} />
              <Typography 
                variant="h4" 
                fontWeight={800} 
                sx={{ 
                  mb: 0.5,
                  textAlign: "left",
                  color: theme.palette.text.primary
                }}
              >
                Manage Users
              </Typography>
            </Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: theme.palette.text.secondary
              }}
            >
              View, edit, and preview generated data for project users.
            </Typography>
          </Box>
          <BglPrimaryButton
            startIcon={<AddIcon />}
            onClick={handleAddNewUser}
            sx={{
              backgroundColor: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                borderColor: theme.palette.primary.dark
              }
            }}
          >
            Add New User
          </BglPrimaryButton>
        </Box>

        {/* Search Controls */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 4,
          py: 3,
          gap: 2
        }}>
          <TextField
            placeholder="Search customer data..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          
          {/* <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
            >
              Export
            </Button>
          </Box> */}
        </Box>

        {/* Table */}
        <Box sx={{ px: 4, pb: 4 }}>
          <TableContainer component={Paper} sx={{ 
            maxHeight: 600, 
            borderRadius: 1, 
            boxShadow: 1, 
            bgcolor: theme.palette.background.paper,
            border: '1px solid',
            borderColor: theme.palette.divider
          }}>
            <Table stickyHeader size="medium" sx={{ border: '1px solid', borderColor: theme.palette.divider }}>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.background.default }}>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Preview</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Persona Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Persona Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>User Attribute</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>User Creation Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Daily Ingestion Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>First Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Last Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Primary Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Secondary Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Meter Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>DCX Physical Clusters</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Rate Plan</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Billing Cycle</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>BB Duration</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', maxWidth: 150 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Zip Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Source Pilot ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Source UUID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>UUID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {error ? (
                  <TableRow>
                    <TableCell colSpan={21} align="center" sx={{ py: 4 }}>
                      <Alert severity="error">{error}</Alert>
                    </TableCell>
                  </TableRow>
                ) : info ? (
                  <TableRow>
                    <TableCell colSpan={21} align="center" sx={{ py: 4 }}>
                      <Alert severity="info">{info}</Alert>
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={21} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No users found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row) => {
                    const hasEmailSession = !!emailSessions[row.id];
                    const isEmailProcessing = emailSessions[row.id]?.isProcessing;
                    return (
                    <TableRow
                      key={row.id}
                      sx={{
                        '&:hover': { bgcolor: theme.palette.action.hover },
                        bgcolor: theme.palette.background.paper
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Preview Dashboard">
                            <IconButton
                              onClick={() => handlePreviewDashboard(row)}
                              color="primary"
                              size="small"
                            >
                              <RemoveRedEyeOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy Dashboard URL">
                            <IconButton
                              onClick={() => handleCopyDashboardUrl(row)}
                              color="primary"
                              size="small"
                              disabled={copyingUserId === row.id}
                            >
                              {copyingUserId === row.id ? (
                                <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />
                              ) : (
                                <ContentCopyRoundedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              hasEmailSession
                                ? isEmailProcessing
                                  ? 'Dispatch in progress'
                                  : 'Review Results'
                                : 'Trigger Emails'
                            }
                          >
                            <IconButton
                              onClick={() => handleOpenEmailDialog(row)}
                              color="primary"
                              size="small"
                              sx={{
                                position: 'relative',
                                ...(hasEmailSession
                                  ? { bgcolor: 'action.selected' }
                                  : {}),
                                ...(isEmailProcessing
                                  ? {
                                      '@keyframes emailBounce': {
                                        '0%, 100%': { transform: 'translateY(0)' },
                                        '50%': { transform: 'translateY(-3px)' }
                                      },
                                      animation: 'emailBounce 1s ease-in-out infinite'
                                    }
                                  : {})
                              }}
                            >
                              <MailOutlineIcon fontSize="small" />
                              {hasEmailSession && (
                                <Box
                                  component="span"
                                  sx={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: isEmailProcessing ? 'info.main' : 'success.main',
                                    boxShadow: `0 0 0 1px ${theme.palette.background.paper}`
                                  }}
                                />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {editingField?.rowId === row.id && editingField?.field === 'personaName' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              sx={{ minWidth: 120 }}
                            />
                            <Tooltip title="Save">
                              <IconButton size="small" onClick={() => handleEditSave(row.id, 'personaName')} color="primary">
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={handleEditCancel} color="error">
                                <BlockIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>{row.personaName}</Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditStart(row.id, 'personaName', row.personaName)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingField?.rowId === row.id && editingField?.field === 'personaDescription' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              sx={{ minWidth: 200 }}
                            />
                            <Tooltip title="Save">
                              <IconButton size="small" onClick={() => handleEditSave(row.id, 'personaDescription')} color="primary">
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={handleEditCancel} color="error">
                                <BlockIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.personaDescription}
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditStart(row.id, 'personaDescription', row.personaDescription)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          if (userAttributesLoading) {
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">
                                  Loading...
                                </Typography>
                              </Box>
                            );
                          }
                          const values = userAttributes[row.id];
                          if (!values || values.length === 0) {
                            return <Typography variant="body2">Not Available</Typography>;
                          }
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {values.map((val, idx) => (
                                <Chip
                                  key={idx}
                                  label={val}
                                  size="small"
                                  variant="outlined"
                                  sx={{ borderColor: '#1976d2', color: '#1976d2' }}
                                />
                              ))}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {row.createdAt}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const hist = ingestionHistory[row.id];
                          const isHistoryEmpty = !!hist && (!hist.user?.length && !hist.raw?.length && !hist.billing?.length);
                          const rawOriginal = row.ingestionStatus;
                          const raw = (rawOriginal || '').toLowerCase();
                          let normalized: 'success' | 'error' | 'not_started' = 
                            raw.includes('success') ? 'success' : 
                            (raw.includes('fail') || raw.includes('error') || raw === 'failed' || raw === 'failure') ? 'error' : 
                            'not_started';
                          if (isHistoryEmpty) normalized = 'not_started';
                          let label = getStatusLabel(normalized as any);
                          let color = getStatusColor(normalized as any);
                          if (rawOriginal === '' || rawOriginal == null) {
                            label = 'Not Started';
                            color = getStatusColor('not_started' as any);
                          }
                          return (
                            <Chip 
                              label={label}
                              size="small"
                              color={color}
                              variant="outlined"
                              icon={label === 'Complete' ? <CheckCircleOutlineIcon fontSize="small" /> : label === 'Incomplete' ? <CloseIcon fontSize="small" /> : undefined}
                              onClick={() => handleIngestionStatusClick(row, 'raw')}
                              sx={{ 
                                fontSize: '0.75rem', 
                                height: '24px',
                                minWidth: '120px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                '&:hover': {
                                  ...getHoverStylesByStatus(normalized)
                                }
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      
                      <TableCell>
                        {editingField?.rowId === row.id && editingField?.field === 'firstName' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              error={!!(editValue && !isValidName(editValue))}
                              helperText={editValue && !isValidName(editValue) ? 'Only letters, spaces, hyphens, and apostrophes allowed' : ''}
                              sx={{ minWidth: 120 }}
                            />
                            <Tooltip title="Save">
                              <IconButton size="small" onClick={() => handleEditSave(row.id, 'firstName')} color="primary">
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={handleEditCancel} color="error">
                                <BlockIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>{row.firstName}</Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditStart(row.id, 'firstName', row.firstName)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingField?.rowId === row.id && editingField?.field === 'lastName' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              error={!!(editValue && !isValidName(editValue))}
                              helperText={editValue && !isValidName(editValue) ? 'Only letters, spaces, hyphens, and apostrophes allowed' : ''}
                              sx={{ minWidth: 120 }}
                            />
                            <Tooltip title="Save">
                              <IconButton size="small" onClick={() => handleEditSave(row.id, 'lastName')} color="primary">
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={handleEditCancel} color="error">
                                <BlockIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>{row.lastName}</Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditStart(row.id, 'lastName', row.lastName)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingField?.rowId === row.id && editingField?.field === 'primaryEmail' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              error={!!(editValue && !isValidEmail(editValue))}
                              helperText={editValue && !isValidEmail(editValue) ? 'Invalid email format' : ''}
                              sx={{ minWidth: 150 }}
                            />
                            <Tooltip title="Save">
                              <IconButton size="small" onClick={() => handleEditSave(row.id, 'primaryEmail')} color="primary">
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={handleEditCancel} color="error">
                                <BlockIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography color={row.primaryEmail && !isValidEmail(row.primaryEmail) ? 'error' : 'inherit'}>
                              {row.primaryEmail}
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditStart(row.id, 'primaryEmail', row.primaryEmail)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingField?.rowId === row.id && editingField?.field === 'secondaryEmail' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              error={!!(editValue && !isValidEmail(editValue))}
                              helperText={editValue && !isValidEmail(editValue) ? 'Invalid email format' : ''}
                              sx={{ minWidth: 150 }}
                            />
                            <Tooltip title="Save">
                              <IconButton size="small" onClick={() => handleEditSave(row.id, 'secondaryEmail')} color="primary">
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={handleEditCancel} color="error">
                                <BlockIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography color={row.secondaryEmail && !isValidEmail(row.secondaryEmail) ? 'error' : 'inherit'}>
                              {row.secondaryEmail}
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditStart(row.id, 'secondaryEmail', row.secondaryEmail)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {row.meterType ? row.meterType.split(', ').map((meterType: string, index: number) => (
                            <Chip
                              key={index}
                              label={meterType}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.7rem', 
                                height: '20px',
                                backgroundColor: theme.palette.primary.light + '20',
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.dark
                              }}
                            />
                          )) : (
                            <Chip 
                              label="" 
                              size="small" 
                              variant="outlined"
                              sx={{ borderColor: '#585858', color: '#585858' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(Array.isArray(row.dcxClusters) 
                              ? row.dcxClusters 
                              : (row.dcxClusters ? String(row.dcxClusters).split(',').map(s => s.trim()).filter(Boolean) : [])
                            ).map((cluster: string, index: number) => {
                              // Extract only the name part before the dash
                              const clusterName = cluster.includes(' - ') ? cluster.split(' - ')[0] : cluster;
                              return (
                                <Chip 
                                  key={index}
                                  label={clusterName} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ borderColor: '#1976d2', color: '#1976d2' }}
                                />
                              );
                            })}
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDcxClusterModalOpen(row)}
                            sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={row.ratePlan} 
                            size="small" 
                            variant="outlined"
                            sx={{ borderColor: '#1976d2', color: '#1976d2' }}
                          />
                          {/* Rate plan editing disabled for now */}
                          <IconButton 
                            size="small" 
                            onClick={() => handleRatePlanModalOpen(row)}
                            sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                    </TableCell>
                      <TableCell>{row.billingCycle}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(() => {
                            const duration = row.bbDuration || '0';
                            if (duration && !duration.toLowerCase().includes('month')) {
                              return `${duration} Month`;
                            }
                            return duration || '0 Month';
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        maxWidth: editingField?.rowId === row.id && editingField?.field === 'address' ? 'none' : 150, 
                        wordWrap: 'break-word',
                        width: editingField?.rowId === row.id && editingField?.field === 'address' ? 'auto' : undefined,
                        minWidth: editingField?.rowId === row.id && editingField?.field === 'address' ? 250 : undefined
                      }}>
                        {editingField?.rowId === row.id && editingField?.field === 'address' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <TextField
                              size="small"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              sx={{ minWidth: 200, flex: 1, maxWidth: '100%' }}
                            />
                            <Tooltip title="Save">
                              <IconButton size="small" onClick={() => handleEditSave(row.id, 'address')} color="primary">
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={handleEditCancel} color="error">
                                <BlockIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ 
                              maxWidth: 120, 
                              maxHeight: 60, 
                              overflow: 'auto',
                              wordWrap: 'break-word',
                              fontSize: '0.875rem',
                              '&::-webkit-scrollbar': {
                                width: '4px',
                                height: '4px'
                              },
                              '&::-webkit-scrollbar-track': {
                                backgroundColor: 'transparent'
                              },
                              '&::-webkit-scrollbar-thumb': {
                                backgroundColor: theme.palette.action.disabled,
                                borderRadius: '2px',
                                '&:hover': {
                                  backgroundColor: theme.palette.action.disabledBackground
                                }
                              }
                            }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: 1.4 }}>
                                {row.address}
                              </Typography>
                            </Box>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditStart(row.id, 'address', row.address)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 }, flexShrink: 0 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingField?.rowId === row.id && editingField?.field === 'zipCode' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              sx={{ minWidth: 80 }}
                            />
                            <Tooltip title="Save">
                              <IconButton size="small" onClick={() => handleEditSave(row.id, 'zipCode')} color="primary">
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton size="small" onClick={handleEditCancel} color="error">
                                <BlockIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>{row.zipCode}</Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditStart(row.id, 'zipCode', row.zipCode)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{row.sourcePilotId}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {row.sourceUUID}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {row.uuid}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDelete(row.id)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={sortedData?.length || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            sx={{ mt: 2 }}
          />
        </Box>
        </Card>
      </Box>

      {/* Description Section */}
      <Box sx={{ 
        mt: 3,
        p: 3,
        maxWidth: 1400,
        mx: 'auto'
      }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          This tool allows generation and management of customer data for testing and demonstration purposes.
        </Typography>
      </Box>

      {/* DCX Physical Cluster Modal */}
      <Dialog 
        open={dcxClusterModalOpen} 
        onClose={handleDcxClusterModalClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
          px: 5
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Select DCX Physical Clusters - {selectedUserForModal?.firstName} {selectedUserForModal?.lastName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select DCX Physical Clusters for this user
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 5 }}>
          {clustersLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <CircularProgress size={20} sx={{ mr: 2 }} />
              <Typography>Loading clusters...</Typography>
            </Box>
          ) : clusters.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No Physical cluster found
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {clusters.map((cluster) => (
                <Button
                  key={cluster}
                  variant={editArray.includes(cluster) ? 'contained' : 'outlined'}
                  onClick={() => handleDcxClusterToggle(cluster)}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.light,
                    },
                    '&.Mui-contained': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  }}
                >
                  {cluster}
                </Button>
              ))}
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selected Clusters: {editArray.length}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: 2,
          px: 5,
          pb: 3
        }}>
          <Button 
            onClick={handleDcxClusterModalClose} 
            variant="outlined"
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderColor: theme.palette.primary.main,
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDcxClusterSave}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rate Plan Modal */}
      <Dialog 
        open={ratePlanModalOpen} 
        onClose={handleRatePlanModalClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
          px: 3,
          pt: 3,
          bgcolor: theme.palette.background.paper
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Select Rate Plan - {selectedUserForModal?.firstName} {selectedUserForModal?.lastName}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, px: 3, pb: 2 }}>
          {/* Current Plan Section - Single Box */}
          <Box sx={{ 
            mb: 3,
            p: 3,
            bgcolor: theme.palette.background.default,
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            }
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2
            }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                color: theme.palette.text.primary,
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Box sx={{ 
                  width: 6, 
                  height: 6, 
                  borderRadius: '50%', 
                  bgcolor: theme.palette.primary.main 
                }} />
                Current Plan
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-1px)'
              }
            }}>
              <Box sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                bgcolor: theme.palette.success.main,
                boxShadow: `0 0 0 3px ${theme.palette.success.main}20`
              }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 700, 
                  color: theme.palette.text.primary,
                  mb: 0.5,
                  fontSize: '1.25rem'
                }}>
                  {selectedUserForModal?.ratePlan || 'No Plan Assigned'}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: theme.palette.text.secondary,
                  fontSize: '0.875rem'
                }}>
                  Active rate plan
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Available Plans Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              mb: 2, 
              color: theme.palette.text.primary
            }}>
              Available Plans
            </Typography>
            {ratePlansLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <Typography>Loading rate plans...</Typography>
              </Box>
            ) : (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={selectedRatePlan}
                  onChange={(e) => setSelectedRatePlan(e.target.value)}
                >
                  {ratePlans
                    .filter(plan => plan !== selectedUserForModal?.ratePlan)
                    .map((plan) => (
                    <FormControlLabel
                      key={plan}
                      value={plan}
                      control={<Radio sx={{ color: theme.palette.primary.main }} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {plan}
                            </Typography>
                            {plan.includes('-') && (
                              <Typography variant="caption" sx={{ 
                                color: theme.palette.text.secondary,
                                display: 'block',
                                mt: 0.5
                              }}>
                                {plan.split('-')[1]?.trim() || 'Rate Plan'}
                              </Typography>
                            )}
                          </Box>
                          <Tooltip title={plan.includes('-') ? `Plan Name: ${plan.split('-')[0]?.trim() || 'N/A'}` : 'Rate plan information'}>
                            <InfoIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                          </Tooltip>
                        </Box>
                      }
                      sx={{ 
                        mb: 1,
                        p: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&.Mui-checked': {
                          borderColor: theme.palette.primary.main,
                          backgroundColor: theme.palette.primary.light + '20',
                        }
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Change History Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HistoryIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600, 
                color: theme.palette.text.primary
              }}>
                Change History
              </Typography>
            </Box>
            {rateHistoryLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <Typography>Loading rate history...</Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {ratePlanHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No rate history found
                  </Typography>
                ) : (
                  ratePlanHistory.map((history, index) => (
                    <Box 
                      key={index}
                      sx={{ 
                        p: 2, 
                        mb: 1, 
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        bgcolor: theme.palette.background.default
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {history.planName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {history.assignedDate}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {history.description}
                      </Typography>
                      {/* {history.endDate && (
                        <Typography variant="caption" color="text.secondary">
                          Ended: {history.endDate}
                        </Typography>
                      )} */}
                    </Box>
                  ))
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: 2,
          px: 3,
          pb: 3
        }}>
          <Button 
            onClick={handleRatePlanModalClose} 
            variant="outlined"
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderColor: theme.palette.primary.main,
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRatePlanSave}
            variant="contained"
            disabled={!selectedRatePlan || selectedRatePlan === selectedUserForModal?.ratePlan || loading}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              '&:disabled': {
                backgroundColor: theme.palette.primary.light,
                color: 'white',
              }
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Updating...
                </Typography>
              </Box>
            ) : (
              'Assign'
            )}
          </Button>
        </DialogActions>
        
        {/* Loading Overlay */}
        <BglOverlayLoader 
          open={loading}
          message="Updating rate plan..."
          backgroundColor="rgba(255, 255, 255, 0.8)"
        />
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmModalOpen}
        onClose={() => setDeleteConfirmModalOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle 
          id="delete-dialog-title"
          sx={{ 
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: 2,
            px: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
            Confirm Deletion
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Alert severity="warning" sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                Are you sure you want to delete this user?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Name:</strong> {userToDelete?.firstName} {userToDelete?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Email:</strong> {userToDelete?.primaryEmail}
              </Typography>
              {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                This action cannot be undone and will permanently remove the user from the system.
              </Typography> */}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: 2, 
          px: 5, 
          pb: 3,
          gap: 2
        }}>
          <Button 
            onClick={() => {
              setDeleteConfirmModalOpen(false);
              setUserToDelete(null);
            }} 
            variant="outlined"
            sx={{
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              px: 3,
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderColor: theme.palette.primary.main,
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              if (userToDelete) {
                try {
                  const deleteResponse = await PilotApis.deleteUserMetadata(rc, parseInt(userToDelete.id));
                  if (deleteResponse.success) {
                    setCustomerData(prev => prev.filter(item => item.id !== userToDelete.id));
                    toast.success('User deleted successfully');
                  } else {
                    toast.error(`Failed to delete user: ${deleteResponse.error}`);
                  }
                } catch (error) {
                  console.error('Error deleting user:', error);
                  toast.error('Failed to delete user');
                }
              }
              setDeleteConfirmModalOpen(false);
              setUserToDelete(null);
            }} 
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              px: 3,
              '&:hover': {
                backgroundColor: theme.palette.error.dark,
              }
            }}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ingestion Status Modal */}
      <Dialog 
        open={ingestionModalOpen} 
        onClose={handleIngestionModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
          px: 3,
          pt: 3,
          bgcolor: theme.palette.background.paper,
          position: 'relative'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SignalCellularAltIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Daily Ingestion Status - {selectedUserForIngestionModal?.firstName} {selectedUserForIngestionModal?.lastName}
            </Typography>
          </Box>
          <IconButton
            onClick={handleIngestionModalClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, px: 3, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Last 30 days of Ingestion Status and Last 90 days of consumption Data
          </Typography>
          
          {/* Job Type Dropdown */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              User Data Type:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedJobTypeForModal}
                onChange={(e) => setSelectedJobTypeForModal(e.target.value)}
                displayEmpty
              >
                <MenuItem value="raw_data_sync">Consumption Data</MenuItem>
                {/* <MenuItem value="user_details_sync">User Data</MenuItem> */}
                <MenuItem value="billing_data_sync">Billing Data</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          {ingestionHistoryLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <CircularProgress size={20} sx={{ mr: 2 }} />
              <Typography>Loading ingestion history...</Typography>
            </Box>
          ) : (() => {
            if (!selectedUserForIngestionModal) return null;
            
            const userHistory = ingestionHistory[selectedUserForIngestionModal.id];
            if (!userHistory) return null;
            
            const jobTypeMap: { [key: string]: 'user' | 'raw' | 'billing' } = {
              'user_details_sync': 'user',
              'raw_data_sync': 'raw',
              'billing_data_sync': 'billing'
            };
            
            const jobTypeKey = jobTypeMap[selectedJobTypeForModal] || 'raw';
            const jobHistory = userHistory[jobTypeKey] || [];
            
            // Filter last 30 days and sort by response_time descending
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            
            const filteredHistory = jobHistory
              .filter((record: any) => {
                if (!record.response_time) return false;
                const recordDate = new Date(record.response_time);
                return recordDate >= thirtyDaysAgo;
              })
              .sort((a: any, b: any) => 
                new Date(b.response_time).getTime() - new Date(a.response_time).getTime()
              );
            
            return (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {filteredHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No ingestion history found for the selected job type.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ 
                    boxShadow: 'none',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1
                  }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: theme.palette.background.default }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Sync Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredHistory.map((record: any, index: number) => (
                          <TableRow
                            key={index}
                            sx={{
                              '&:hover': { bgcolor: theme.palette.action.hover },
                              bgcolor: theme.palette.background.paper
                            }}
                          >
                            <TableCell sx={{ fontSize: '0.8rem' }}>
                              {record.response_time ? new Date(record.response_time).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }) : 'Unknown Date'}
                            </TableCell>
                            <TableCell>
                          {(() => {
                            const rawStatus = (record.response_payload?.status || 'not_started').toLowerCase();
                            const normalizedStatus: 'success' | 'error' | 'not_started' = 
                              rawStatus.includes('success') ? 'success' : 
                              (rawStatus.includes('fail') || rawStatus.includes('error') || rawStatus === 'failed' || rawStatus === 'failure') ? 'error' : 
                              'not_started';
                            return (
                              <Chip 
                                label={getStatusLabel(normalizedStatus)}
                                size="small"
                                color={getStatusColor(normalizedStatus)}
                                variant="outlined"
                                icon={getStatusLabel(normalizedStatus) === 'Complete' ? <CheckCircleOutlineIcon fontSize="small" /> : getStatusLabel(normalizedStatus) === 'Incomplete' ? <CloseIcon fontSize="small" /> : undefined}
                                sx={{ fontSize: '0.75rem', '&:hover': { ...getHoverStylesByStatus(normalizedStatus) } }}
                              />
                            );
                          })()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            );
          })()}

          {/* Daily Consumption Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            mt: 4,
            pb: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            pt: 3
          }}>
            <Typography variant="h2" sx={{ 
              fontSize: '1.5rem', 
              fontWeight: 600, 
              color: theme.palette.text.primary,
              textAlign: 'left'
            }}>
              Daily Consumption (Last 90 Days)
            </Typography>
            
            {selectedUserForIngestionModal && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <Select
                  value={selectedMeterFuel}
                  onChange={(e) => handleMeterFuelChange(String(e.target.value))}
                  displayEmpty
                  sx={{
                    height: 32,
                    '& .MuiSelect-select': {
                      py: 0.5,
                      textAlign: 'center'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                >
                  <MenuItem value="">
                    <Box sx={{ width: '100%', textAlign: 'center' }}>
                      <em>Select Meter Fuel</em>
                    </Box>
                  </MenuItem>
                  {(() => {
                    // Extract meter fuel types from meterType (comma-separated string)
                    const meterFuelTypes = selectedUserForIngestionModal.meterType 
                      ? selectedUserForIngestionModal.meterType.split(', ').filter(Boolean)
                      : [];
                    
                    return meterFuelTypes.map((fuel: string) => (
                      <MenuItem key={fuel} value={fuel} sx={{ justifyContent: 'center' }}>
                        <Box sx={{ width: '100%', textAlign: 'center' }}>{fuel}</Box>
                      </MenuItem>
                    ));
                  })()}
                </Select>
              </FormControl>
            )}
          </Box>

          {/* Consumption Bar Chart */}
          {consumptionLoading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              py: 4,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              bgcolor: theme.palette.background.default
            }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading consumption data...
              </Typography>
            </Box>
          ) : consumptionData.length > 0 ? (
            <>
              <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600 }}>
                Daily Consumption (Last 3 Months) - {selectedMeterFuel}
              </Typography>

              {/* Chart with Recharts */}
              {(() => {
                const chartWidth = Math.max(800, consumptionData.length * 12);
                
                // Calculate Y-axis domain for fixed axis
                const maxKwh = Math.max(...consumptionData.map((d: any) => d.consumptionKwh));
                const minKwh = Math.min(...consumptionData.map((d: any) => d.consumptionKwh));
                
                const range = Math.max(Math.abs(maxKwh), Math.abs(minKwh));
                let tickInterval: number;
                
                if (range <= 20) {
                  tickInterval = 2;
                } else if (range <= 40) {
                  tickInterval = 4;
                } else if (range <= 80) {
                  tickInterval = 8;
                } else if (range <= 120) {
                  tickInterval = 16;
                } else {
                  tickInterval = 20;
                }
                
                const padding = tickInterval;
                let yMax = Math.ceil((maxKwh + padding) / tickInterval) * tickInterval;
                let yMin = Math.floor((minKwh - padding) / tickInterval) * tickInterval;
                
                if (minKwh >= 0) {
                  yMin = 0;
                }
                if (maxKwh <= 0) {
                  yMax = 0;
                }
                
                // Generate Y-axis ticks including 0
                const yTicks: number[] = [];
                for (let t = yMin; t <= yMax; t += tickInterval) {
                  yTicks.push(t);
                }
                
                // Ensure 0 is always included in ticks
                if (!yTicks.includes(0) && yMin < 0 && yMax > 0) {
                  yTicks.push(0);
                  yTicks.sort((a, b) => a - b);
                }
                
                // Get first date of each unique month for X-axis ticks
                const monthTicks = consumptionData
                  .reduce((acc: any, curr: any) => {
                    const date = new Date(curr.date);
                    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
                    const existing = acc.find((item: any) => item.monthKey === monthKey);
                    if (!existing) {
                      acc.push({ date: curr.date, monthKey });
                    } else {
                      // Keep the earliest date in the month
                      const existingDate = new Date(existing.date);
                      if (date < existingDate) {
                        existing.date = curr.date;
                      }
                    }
                    return acc;
                  }, [])
                  .map((item: any) => item.date)
                  .sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime());
                
                return (
                  <Box 
                    ref={chartScrollContainerRef}
                    sx={{
                      height: 280,
                      width: '100%',
                      overflow: 'auto',
                      '&::-webkit-scrollbar': {
                        width: 8,
                        height: 8,
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.background.default,
                        borderRadius: 4,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.action.disabled,
                        borderRadius: 4,
                        '&:hover': {
                          backgroundColor: theme.palette.action.disabledBackground,
                        },
                      },
                    }}
                  >
                    <Box sx={{ width: chartWidth, height: 280 }}>
                      <BarChart
                        width={chartWidth}
                        height={280}
                        data={consumptionData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                        barCategoryGap="4%"
                      >
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => {
                            const date = new Date(value);
                            return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                          }}
                          ticks={monthTicks}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                          tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                        />
                        <YAxis
                          label={{ 
                            value: 'kWh', 
                            angle: -90, 
                            position: 'insideLeft', 
                            style: { 
                              textAnchor: 'middle', 
                              fill: theme.palette.text.secondary,
                              fontSize: '0.75rem',
                              fontWeight: 500
                            } 
                          }}
                          tick={{ 
                            fontSize: 11, 
                            fill: theme.palette.text.secondary,
                            fontWeight: 400
                          }}
                          domain={[yMin, yMax]}
                          ticks={yTicks}
                          allowDecimals={false}
                          tickLine={{ stroke: theme.palette.divider, strokeWidth: 1 }}
                          axisLine={{ stroke: theme.palette.divider, strokeWidth: 1 }}
                          width={50}
                        />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              const date = new Date(data.date);
                              const fullDate = date.toISOString().split('T')[0];
                              return (
                                <Box
                                  sx={{
                                    backgroundColor: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 1,
                                    p: 1,
                                    boxShadow: 2
                                  }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {fullDate}
                                  </Typography>
                                  <Typography variant="body2" color="primary">
                                    {Number(payload[0].value || 0).toFixed(1)} kWh
                                  </Typography>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar
                          dataKey="consumptionKwh"
                          fill={theme.palette.primary.main}
                          radius={[2, 2, 0, 0]}
                          barSize={8}
                          onMouseEnter={(data, index) => {
                            setHoveredBarIndex(index);
                          }}
                          onMouseLeave={() => {
                            setHoveredBarIndex(null);
                          }}
                        >
                          {consumptionData.map((entry: any, index: number) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={hoveredBarIndex === index ? theme.palette.primary.light : theme.palette.primary.main}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </Box>
                  </Box>
                );
              })()}
            </>
          ) : selectedMeterFuel ? (
            <Box sx={{ 
              mt: 3,
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              bgcolor: theme.palette.background.paper,
              textAlign: 'center'
            }}>
              <Typography variant="body2" color="text.secondary">
                {selectedMeterFuel.startsWith('NSM') 
                  ? 'No Daily Consumption data is available for NSM Meter'
                  : 'No consumption data available for the selected meter fuel'}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        
      </Dialog>

      <EmailTriggerDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        user={
          selectedUserForEmail
            ? {
                id: selectedUserForEmail.id,
                displayName: getEmailTriggerDisplayName(selectedUserForEmail)
              }
            : null
        }
        session={selectedUserForEmail ? emailSessions[selectedUserForEmail.id] ?? null : null}
        onStart={handleStartEmailDispatch}
        onAcknowledge={handleAcknowledgeEmailSession}
        emails={applicableEmails}
        emailsLoading={emailsLoading}
        emailsError={emailsError}
      />

      <BglOverlayLoader
        open={initialLoading}
        message="Loading customer data..."
        size={50}
        backgroundColor="rgba(255, 255, 255, 0.4)"
        zIndex={1400}
      />
    </Box>
  );
};

